import { useCallback, useMemo, useState } from 'react';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { ActionIcon, Button, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { DocumentTypeService } from '../../../services/document-type/documentTypeService';
import { DocumentType, DocumentTypesApiResponse } from '../../../type/entities/document-type';
import DocumentTypeModalForm, { documentTypeEntityOptions } from './DocumentTypeModalForm';

const columnOrder = [
    'id',
    'name',
    'entity',
    'immutable',
];

const DocumentTypesList = () => {
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const documentTypeService = useMemo(() => new DocumentTypeService(), []);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null);

    const [documentTypes, loading, , refetch] = useFetch(useCallback(async () => {
        const response = await documentTypeService.listDocumentTypes(filters);
        return response.getResponseData() as DocumentTypesApiResponse;
    }, [filters, documentTypeService]));

    const getEntityLabel = (entity: string) => {
        return documentTypeEntityOptions.find((option) => option.value === entity)?.label ?? entity;
    };

    const handleDelete = useCallback(async (id: string, immutable: boolean) => {
        if (immutable) {
            toast.error('Este tipo de documento es inmutable y no se puede eliminar');
            return;
        }

        try {
            const response = await (await documentTypeService.deleteDocumentType(id)).getResponseData();
            if (response.success) {
                toast.success('Tipo de documento eliminado correctamente');
                refetch();
                return;
            }

            handleErrors(response);
        } catch (error: any) {
            toast.error(error?.message ?? 'Error al eliminar el tipo de documento');
        }
    }, [documentTypeService, handleErrors, refetch]);

    const handleOpenModal = useCallback((documentType?: DocumentType | null) => {
        setSelectedDocumentType(documentType ?? null);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedDocumentType(null);
    }, []);

    const orderedData = () => documentTypes?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    }) ?? [];

    return (
        <div className="space-y-6">
            {documentTypes !== undefined && (
                <CustomTable
                    largeTable
                    id={'document-types-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    overrideColumns={[
                        {
                            key: 'entity',
                            render: (row: any) => (
                                <span>{getEntityLabel(row.row.original.entity)}</span>
                            ),
                        },
                        {
                            key: 'immutable',
                            render: (row: any) => (
                                <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        row.row.original.immutable ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                                    }`}
                                >
                                    {row.row.original.immutable ? 'Sí' : 'No'}
                                </span>
                            ),
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'document_type', permission: 'edit_document_type' },
                            render: (row: DocumentType) => {
                                if (row.immutable) {
                                    return null;
                                }

                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar tipo de documento'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => handleOpenModal(row)}
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'document_type', permission: 'delete_document_type' },
                            render: (row: DocumentType) => {
                                if (row.immutable) {
                                    return null;
                                }

                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar tipo de documento`}
                                                description={`¿Estás seguro de que deseas eliminar el tipo de documento "${row.name}"?`}
                                                onDelete={() => handleDelete(row.id, row.immutable)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filter={false}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: documentTypes ? documentTypes.lastPage : 1,
                        totalCount: documentTypes?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                >
                    {userCan('create_document_type', 'document_type') && (
                        <Button onClick={() => handleOpenModal()}>
                            Crear tipo de documento
                        </Button>
                    )}
                </CustomTable>
            )}

            <DocumentTypeModalForm
                data={selectedDocumentType}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                refetch={refetch}
            />
        </div>
    );
};

export default DocumentTypesList;
